/**
 * Spotlight în casă - înlocuiește driver.js. Un inel `fixed`, desenat direct
 * din `getBoundingClientRect()` al elementului țintă, într-un shadow root
 * separat (izolare identică widgetului, vezi `widget.ts`). Spre deosebire de
 * driver.js: fără overlay care întunecă pagina - situl gazdă rămâne complet
 * interactiv, promisiunea din prompt-ul agentului ("dai click tu, nu eu")
 * ține literal.
 *
 * Un singur spotlight activ deodată: un nou `highlightElement` demontează
 * complet instanța anterioară (listeners, observer, rAF, nod DOM) înainte
 * să deseneze una nouă.
 *
 * Ținte în `<iframe>`: nu există, prin construcție. `capturePageSnapshot`
 * interoghează doar `document`, iar `document.querySelector` (calea veche, pe
 * selector) nu trece granița unui iframe - deci niciun element dinăuntru nu
 * primește vreodată un `idx` și nu poate ajunge aici. Dacă vreodată enumerăm
 * iframe-uri same-origin, `position: fixed` de aici se raportează la viewportul
 * documentului NOSTRU, deci geometria ar trebui translatată cu poziția
 * iframe-ului; până atunci, nu e un caz de tratat.
 */
import { labelFor } from "./actionTrail";
import { settleDom } from "./settle";

/** Culoarea implicită (violet de brand) - aceeași folosită ca fallback în widget.ts. */
const DEFAULT_PRIMARY = "#8b5cf6";
const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

/** Cât de mult iese inelul în afara cutiei elementului. */
const RING_OFFSET_PX = 10;
/** Cât se adaugă la border-radius-ul propriu al elementului. */
const RADIUS_PADDING_PX = 8;
/** Radius de rezervă, când getComputedStyle nu dă un număr utilizabil (ex. jsdom). */
const FALLBACK_RADIUS_PX = 12;
/** Spațiul dintre inel și bula de etichetă. */
const LABEL_GAP_PX = 10;
/** Marginea minimă păstrată față de marginile viewport-ului. */
const VIEWPORT_MARGIN_PX = 4;

/**
 * Fereastra de grație de după afișare + stabilitatea derulării: dismiss-ul
 * pe scroll se armează abia după `max(shownAt + GRACE, ultimul scroll + STABLE)`.
 * Motivul: `scrollTo` (executor.ts) declanșează `scrollIntoView({behavior:"smooth"})`
 * chiar înainte de un highlight - fără fereastra asta, propriul nostru scroll
 * ar demonta inelul instant.
 */
const SCROLL_ARM_GRACE_MS = 600;
const SCROLL_STABLE_MS = 150;

/**
 * Cât rămâne inelul vizibil dacă vizitatorul nu dă click pe țintă și nu vine
 * un pas nou. Feedback din 1 aug 2026: un click oriunde pe pagină stingea
 * inelul instant, iar vizitatorii nu apucau să-l vadă înainte să interacționeze
 * cu altceva. Feedback din 21 aug 2026 (Andrei): „atenționarea să rămână până
 * la acțiune" - inelul dispărea la primul scroll și, oricum, după 30 s, adică
 * exact cât îi lua să citească replica și să se uite pe pagină. Acum îl sting
 * doar: un click PE elementul evidențiat, un pas nou (`highlightElement` din
 * nou), o întrebare nouă / închiderea explicită a chatului (sdk.ts), și
 * plasa asta de siguranță, aliniată cu `ACTIVATION_TTL_MS`: un inel pe care
 * nimeni nu l-a apăsat în zece minute e un pas uitat, nu unul în curs.
 */
const RING_DISMISS_MS = 600_000;

/** Deasupra restului paginii, dar sub panoul widgetului (`:host` din widget.ts, 2147483000). */
/**
 * SUB toate suprafețele noastre, deliberat (cerut de Radu, 12 aug 2026): orbul
 * și ținta lui de click stau la 2147482992, sticlele (card + bula vocală) la
 * 2147482993, panoul clasic la 2147483000. Inelul care se desena PESTE bula
 * vocală părea că încercuiește textul replicii - conversația e mai importantă
 * decât inelul, deci sticla îl acoperă; ferirea (`avoidChatObstacle`) rămâne
 * calea prin care cele două ajung să nu se suprapună deloc.
 */
const SPOTLIGHT_Z_INDEX = 2147482991;

const SPOTLIGHT_CSS = `
/* Everything below lives inside a shadow root on a host with
   pointer-events: none, so none of it is ever clickable - the ring and the
   label are purely visual and never sit between the visitor and the page. */
/* Nothing here fades opacity in, deliberately. In a backgrounded or
   occluded tab neither requestAnimationFrame nor CSS animations advance, so
   an entrance animation freezes at its first keyframe - leaving the ring
   mounted at opacity: 0 forever: in the DOM, armed for dismissal, and
   invisible to the visitor, who is left with no guidance at all.
   So the ring is opaque from the first paint and only the pulse animates.
   Frozen animations then degrade to a plain static ring, which is fine; a
   230ms fade is not worth a failure mode that silently removes the feature. */
.aig-spotlight-ring {
  position: fixed;
  box-sizing: border-box;
  pointer-events: none;
  border: 3px solid var(--aig-primary, #8b5cf6);
  animation: aig-spotlight-pulse 1.25s ease-in-out infinite;
}
/* The pulse should read as breathing, not a blink: the spread grows while
   its alpha fades out, then retraces the same path back - a symmetric cycle
   with no jump when it loops, matching the sine wave in the launch video.
   A second, unanimated shadow stays underneath at every frame, so the
   element never looks fully "cold" at the peak of the cycle. */
@keyframes aig-spotlight-pulse {
  0%, 100% {
    box-shadow:
      0 0 0 6px rgba(var(--aig-primary-rgb, 139, 92, 246), 0.18),
      0 0 40px rgba(var(--aig-primary-rgb, 139, 92, 246), 0.25);
  }
  50% {
    box-shadow:
      0 0 0 16px rgba(var(--aig-primary-rgb, 139, 92, 246), 0),
      0 0 40px rgba(var(--aig-primary-rgb, 139, 92, 246), 0.25);
  }
}
/* Non-negotiable: a static ring, no pulsing, when the visitor asked the OS
   for reduced motion. */
@media (prefers-reduced-motion: reduce) {
  .aig-spotlight-ring {
    animation: none;
    box-shadow:
      0 0 0 6px rgba(var(--aig-primary-rgb, 139, 92, 246), 0.18),
      0 0 40px rgba(var(--aig-primary-rgb, 139, 92, 246), 0.25);
  }
}
.aig-spotlight-label {
  position: fixed;
  box-sizing: border-box;
  pointer-events: none;
  max-width: 280px;
  background: #fff;
  color: #111;
  border-radius: 10px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
  padding: 8px 12px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 13px;
  line-height: 1.45;
}
.aig-spotlight-label-title {
  font-weight: 600;
  color: var(--aig-primary, #8b5cf6);
}
.aig-spotlight-label-title + div {
  margin-top: 2px;
}
`;

/**
 * Driver.js randa title/description ca HTML - funcția asta scăpa tot ce venea
 * din config sau de la agent. Spotlight-ul nou nu se mai bazează pe ea (bula
 * de etichetă folosește `textContent`, unde escaparea e structural inutilă).
 * Rămâne exportată doar ca plasă de siguranță pentru orice cod viitor care ar
 * scrie HTML din text venit de la agent - azi n-o mai apelează nimeni în afara
 * testelor. Dacă peste câteva luni tot n-are consumator, se șterge.
 */
export function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case "&": return "&amp;";
      case "<": return "&lt;";
      case ">": return "&gt;";
      case '"': return "&quot;";
      default: return "&#39;";
    }
  });
}

/**
 * Culoarea curentă a widgetului, citită de pe `.aig-root` - aceeași
 * mecanică prin care `widget.ts` (`mount()`) o setează pe host când contul
 * are branding propriu. Citim, nu recalculăm: dacă widgetul n-a montat încă
 * sau clientul n-are culoare custom, cădem pe indigo-ul implicit.
 */
function currentPrimaryColor(): string {
  const widgetHost = document.querySelector<HTMLElement>(".aig-root");
  const value = widgetHost?.style.getPropertyValue("--aig-primary").trim();
  return value && HEX_COLOR_RE.test(value) ? value : DEFAULT_PRIMARY;
}

/** `#rrggbb` → `"r, g, b"`, pentru `rgba(var(--aig-primary-rgb), alpha)` din CSS. */
function hexToRgbChannels(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  return `${(n >> 16) & 0xff}, ${(n >> 8) & 0xff}, ${n & 0xff}`;
}

/**
 * Border-radius-ul elementului + padding, sau rezerva fixă când nu se poate citi unul util.
 * Se citește O SINGURĂ DATĂ, la afișare: `getComputedStyle` forțează recalcul de
 * stil, iar raza nu se schimbă la derulare - chemată din bucla de geometrie ar fi
 * costat un reflow în plus la fiecare cadru, pe pagina clientului.
 */
function ringRadiusPx(el: Element): number {
  const raw = getComputedStyle(el).borderRadius;
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) ? parsed + RADIUS_PADDING_PX : FALLBACK_RADIUS_PX;
}

/**
 * Cât de mult din țintă trebuie să rămână efectiv vizibil ca să mai desenăm
 * inelul. Sub pragul ăsta arătăm spre o fâșie pe care vizitatorul n-o poate
 * observa, deci e mai cinstit să nu arătăm nimic.
 */
const MIN_VISIBLE_PX = 4;

/**
 * Strămoșii care taie elementul (`overflow` != `visible`). Se strâng O SINGURĂ
 * DATĂ, la afișare: lanțul nu se schimbă la derulare, iar `getComputedStyle`
 * pe fiecare părinte la fiecare cadru ar fi scump.
 *
 * De ce e nevoie: `getBoundingClientRect()` întoarce cutia elementului chiar
 * și când un container cu `overflow: auto/hidden` îl ascunde complet. Fără
 * verificarea asta, inelul se desenează peste conținut care n-are nicio
 * legătură, arătând spre ceva ce nu se vede - exact opusul îndrumării.
 */
function clippingAncestors(el: Element): Element[] {
  const out: Element[] = [];
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const style = getComputedStyle(p);
    // și scurtătura `overflow`: browserele o rezolvă în overflowX/Y, jsdom nu
    const values = [style.overflow, style.overflowX, style.overflowY];
    if (values.some((v) => v && v !== "visible")) out.push(p);
  }
  return out;
}

/**
 * Cât din țintă rămâne vizibil după ce o intersectăm cu toți strămoșii care o
 * taie și cu viewportul. `null` = nimic de arătat (ascunsă, tăiată complet,
 * sau cu dimensiune zero, cazul `display: none`).
 */
function visibleExtent(instance: SpotlightInstance): { width: number; height: number } | null {
  const rect = instance.target.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;

  let { left, top, right, bottom } = rect;
  for (const ancestor of instance.clippers) {
    const box = ancestor.getBoundingClientRect();
    left = Math.max(left, box.left);
    top = Math.max(top, box.top);
    right = Math.min(right, box.right);
    bottom = Math.min(bottom, box.bottom);
  }
  // Viewportul intră în calcul doar dacă îl putem citi. Un `innerWidth/Height`
  // raportat 0 (context de evaluare fără layout, măsurare înainte de prima
  // așezare) ar tăia orice țintă la zero și ar ascunde inelul mereu - dintr-o
  // necunoaștere, nu dintr-o constatare. Când nu știm, nu tăiem.
  const vw = window.innerWidth || document.documentElement.clientWidth;
  const vh = window.innerHeight || document.documentElement.clientHeight;
  if (vw > 0 && vh > 0) {
    left = Math.max(left, 0);
    top = Math.max(top, 0);
    right = Math.min(right, vw);
    bottom = Math.min(bottom, vh);
  }

  const width = right - left;
  const height = bottom - top;
  return width >= MIN_VISIBLE_PX && height >= MIN_VISIBLE_PX ? { width, height } : null;
}

/** Spațiul liber lăsat între panoul de chat și zona evidențiată, după mutare. */
const OBSTACLE_GAP_PX = 12;
/**
 * Sub lățimea asta (telefon), un panou de chat ocupă ~60% din ecran și nicio
 * deplasare laterală nu-l scoate din calea țintei. Atunci, ca ultimă soluție,
 * panoul se „dochează": coboară până rămâne vizibil doar headerul (vezi
 * `decideAvoidance`). Peste prag, comportamentul rămâne exact cel de dinainte.
 */
const DOCK_MAX_VIEWPORT_PX = 480;
/** Cât din panou rămâne pe ecran când e dochat: headerul, pe care se atinge ca să-l redeschizi. */
const DOCK_PEEK_PX = 44;

/**
 * Panoul de chat, văzut de spotlight ca simplu obstacol dreptunghiular.
 *
 * Deliberat o interfață, nu un import din `widget.ts`: modulul ăsta n-a cunoscut
 * niciodată widgetul (citește doar culoarea de pe `.aig-root`, ca variabilă CSS),
 * iar panoul nici măcar nu stă în cutia acelui host - e `position: fixed` ÎNĂUNTRUL
 * shadow root-ului, deci iese complet din ea. `sdk.ts` deține ambele piese și le
 * leagă; aici rămâne doar aritmetica.
 */
export interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface ChatObstacle {
  /**
   * Cutia panoului în coordonate de viewport, NEDEPLASATĂ - adică fără mutarea
   * pe care i-am cerut-o noi. Altfel a doua evaluare ar măsura propriul nostru
   * efect și ar împinge panoul din nou, la fiecare recalculare. `null` = nimic
   * în cale (panou închis).
   */
  rect(): Box | null;
  /** Deplasează panoul cu (dx, dy) față de locul lui; `(0, 0)` = revenire. */
  shift(dx: number, dy: number): void;
  /**
   * Anunță că panoul și-a schimbat cutia DUPĂ ce inelul s-a aprins - cazul
   * vocii (12 aug 2026, staging): decizia de ferire se lua o dată, la afișarea
   * inelului, dar bula de replică CREȘTE pe măsură ce textul curge în ea și
   * ajungea peste inel. Opțional: panoul clasic are dimensiune fixă, deci nu
   * are ce anunța.
   */
  onChange?(listener: () => void): void;
  /**
   * Panoul a fost coborât până rămâne doar headerul vizibil (telefon, când nu
   * există nicio deplasare care să elibereze ținta) sau a revenit. Opțional:
   * suprafețele care nu au starea asta (orb-chat) n-o implementează.
   */
  setDocked?(docked: boolean): void;
  /**
   * Vizitatorul a atins headerul panoului dochat ca să-l deschidă. Din clipa
   * aceea nu-l mai dochăm pentru ACEST inel: a ales să citească chatul, iar un
   * scroll sau un resize nu are voie să-l ia înapoi sub el.
   */
  onDockRelease?(listener: () => void): void;
}

let chatObstacle: ChatObstacle | null = null;

/** Leagă panoul de chat de spotlight. `null` îl dezleagă (folosit în teste). */
export function setChatObstacle(obstacle: ChatObstacle | null): void {
  chatObstacle = obstacle;
  // Re-evaluarea e idempotentă (vezi antetul `avoidChatObstacle`), deci un
  // anunț în plus nu costă nimic; unul lipsă costa un inel sub bulă.
  obstacle?.onChange?.(() => {
    if (active) avoidChatObstacle(active);
  });
  obstacle?.onDockRelease?.(() => {
    if (active) active.dockReleased = true;
  });
}

/**
 * Motivele pentru care o activare armată (vezi `armedActivation`) se poate
 * dezarma fără să fi fost declanșată de click. Doar cele "abandoned_*" produc
 * telemetrie - "reached" (clickul chiar a activat pasul) și "superseded" (un
 * highlight nou înlocuiește pasul vechi, ca parte a ACELEIAȘI ghidări) nu sunt
 * un abandon, deci nu trec prin sink.
 */
export type SpotlightDisarmReason =
  | "reached"
  | "abandoned_dismissed"
  | "abandoned_expired"
  | "abandoned_new_question"
  | "superseded";

const ABANDON_REASONS = new Set<SpotlightDisarmReason>([
  "abandoned_dismissed",
  "abandoned_expired",
  "abandoned_new_question",
]);

type GuidanceOutcomeSink = (
  outcome: "abandoned_dismissed" | "abandoned_expired" | "abandoned_new_question",
  targetLabel?: string,
) => void;

let guidanceOutcomeSink: GuidanceOutcomeSink | null = null;

/**
 * Leagă sdk.ts (care deține `RuntimeApi`) de dezarmările pornite din acest
 * modul. Doar abandonurile trec pe-aici - "reached" se emite direct din
 * sdk.ts, la punctele unde acolo se știe deja cu certitudine că pasul a fost
 * atins (vezi comentariul din sdk.ts, runAsk).
 */
export function setGuidanceOutcomeSink(sink: GuidanceOutcomeSink | null): void {
  guidanceOutcomeSink = sink;
}

/**
 * Eticheta țintei unei activări armate, pentru telemetrie - NICIODATĂ `value`
 * dintr-un input, aceleași granițe ca `actionTrail.ts` (privacy §2.2, registru
 * → G12). Trunchiată deja la `ACTION_TRAIL_MAX_LABEL_CHARS` de `labelFor`.
 */
function armedTargetLabel(target: Element): string | undefined {
  return labelFor(target);
}

/**
 * Zona pe care panoul n-are voie s-o acopere: inelul plus, dacă are dimensiune
 * citibilă, bula de etichetă. Inelul se recalculează din ținta vie cu aceeași
 * aritmetică din `updateGeometry` - nu se citește din DOM - ca să nu depindem
 * de layout pentru o decizie de layout. Eticheta, în schimb, are lățime dată de
 * text, deci acolo chiar trebuie măsurată; unde măsurarea dă zero (jsdom, sau
 * înainte de prima așezare) o ignorăm, în loc să inventăm o cutie.
 */
function guidedZone(instance: SpotlightInstance): Box {
  const rect = instance.target.getBoundingClientRect();
  const zone: Box = {
    left: rect.left - RING_OFFSET_PX,
    top: rect.top - RING_OFFSET_PX,
    right: rect.right + RING_OFFSET_PX,
    bottom: rect.bottom + RING_OFFSET_PX,
  };
  const label = instance.label?.getBoundingClientRect();
  if (label && label.width > 0 && label.height > 0) {
    zone.left = Math.min(zone.left, label.left);
    zone.top = Math.min(zone.top, label.top);
    zone.right = Math.max(zone.right, label.right);
    zone.bottom = Math.max(zone.bottom, label.bottom);
  }
  return zone;
}

function overlaps(a: Box, b: Box): boolean {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

/**
 * Încape panoul în viewport după ce l-am deplasat cu (dx, dy)? Fără verificarea
 * asta l-am putea „rezolva" scoțându-l pe jumătate din ecran - adică exact
 * problema pe care o reparăm, mutată în altă parte.
 */
function fitsInViewport(panel: Box, dx: number, dy: number): boolean {
  const vw = window.innerWidth || document.documentElement.clientWidth;
  const vh = window.innerHeight || document.documentElement.clientHeight;
  if (vw <= 0 || vh <= 0) return false; // fără viewport citibil nu mutăm nimic
  return (
    panel.left + dx >= VIEWPORT_MARGIN_PX &&
    panel.right + dx <= vw - VIEWPORT_MARGIN_PX &&
    panel.top + dy >= VIEWPORT_MARGIN_PX &&
    panel.bottom + dy <= vh - VIEWPORT_MARGIN_PX
  );
}

/** Ce să facă panoul față de zona evidențiată. */
export interface Avoidance {
  dx: number;
  dy: number;
  /** Panoul coboară până rămâne doar headerul (telefon). */
  docked: boolean;
}

/**
 * Decizia de ferire, ca funcție pură de geometrie (testată fără DOM):
 *   1. zona nu atinge panoul -> `{0, 0}`, revenire acasă;
 *   2. o deplasare care încape în ecran, cea mai scurtă (lateralul primul);
 *   3. pe un viewport îngust (<= 480px) și doar dacă `canDock`: panoul coboară
 *      până rămâne vizibil headerul, cu condiția ca banda aceea să nu acopere ea
 *      zona evidențiată;
 *   4. altfel `null` - nu mutăm nimic și nu spunem nimic.
 *
 * Pasul 3 e ales în locul derulării paginii pentru că nu depinde de lungimea
 * paginii (la capăt nu mai poți derula) și funcționează pentru ținte înalte
 * (un card de preț), pe care zona liberă de deasupra panoului nu le încape.
 * Coboară panoul, nu îl strânge: aceeași mecanică `transform` ca ferirea
 * laterală, deci rect-ul „acasă" rămâne corect și decizia e idempotentă.
 */
export function decideAvoidance(panel: Box, zone: Box, canDock: boolean): Avoidance | null {
  if (!overlaps(panel, zone)) return { dx: 0, dy: 0, docked: false };

  // Lateralul primul: la deplasare egală, un pas în lateral se citește mai
  // calm decât unul pe verticală. `sort` e stabil, deci ordinea asta ține.
  const candidates: Array<{ dx: number; dy: number }> = [
    { dx: zone.right + OBSTACLE_GAP_PX - panel.left, dy: 0 },
    { dx: zone.left - OBSTACLE_GAP_PX - panel.right, dy: 0 },
    { dx: 0, dy: zone.bottom + OBSTACLE_GAP_PX - panel.top },
    { dx: 0, dy: zone.top - OBSTACLE_GAP_PX - panel.bottom },
  ];
  const viable = candidates
    .filter((c) => fitsInViewport(panel, c.dx, c.dy))
    .sort((a, b) => Math.abs(a.dx + a.dy) - Math.abs(b.dx + b.dy));
  if (viable.length > 0) return { ...viable[0], docked: false };

  const vw = window.innerWidth || document.documentElement.clientWidth;
  const vh = window.innerHeight || document.documentElement.clientHeight;
  if (!canDock || vw <= 0 || vh <= 0 || vw > DOCK_MAX_VIEWPORT_PX) return null;
  const dy = vh - DOCK_PEEK_PX - panel.top;
  if (dy <= 0) return null;
  const strip: Box = { left: panel.left, right: panel.right, top: vh - DOCK_PEEK_PX, bottom: vh };
  if (overlaps(strip, zone)) return null;
  return { dx: 0, dy, docked: true };
}

/**
 * Ținta e sub panoul de chat? Atunci dă-te la o parte - pe drumul cel mai scurt
 * care chiar există (vezi `decideAvoidance`).
 *
 * Nu poate oscila: panoul care se mută nu schimbă poziția țintei, deci nu există
 * buclă de reacție. Și e idempotentă - `rect()` întoarce cutia nedeplasată, deci
 * rulată de două ori pentru același highlight dă exact aceeași deplasare, iar
 * `shift()` a doua oară nu mai are ce anunța.
 *
 * Când nimic nu ajută (decizia e `null`), nu mutăm și nu spunem nimic: un „mă
 * dau la o parte" urmat de nemișcare e mai rău decât tăcerea.
 */
function avoidChatObstacle(instance: SpotlightInstance): void {
  const obstacle = chatObstacle;
  if (!obstacle) return;
  const panel = obstacle.rect();
  if (!panel) return;
  const decision = decideAvoidance(panel, guidedZone(instance), !instance.dockReleased);
  if (!decision) return;
  obstacle.shift(decision.dx, decision.dy);
  obstacle.setDocked?.(decision.docked);
}

/**
 * Aduce ținta în ecran dacă nu încape întreagă în viewport.
 *
 * Nu e cosmetică: `visibleExtent` clampează inelul la viewport, deci o țintă
 * de sub linia de plutire n-ar produce absolut nimic - highlight fără derulare
 * = ecran gol. Driver.js derula automat; la înlocuirea lui comportamentul s-a
 * pierdut, iar decupajul l-a transformat din „inel undeva în afara ecranului"
 * în „nimic". Acțiunea `scrollTo` (executor.ts) rămâne utilă separat, pentru
 * când agentul vrea derulare FĂRĂ inel.
 *
 * Derularea e `smooth`, iar fereastra de grație de la armarea dismiss-ului e
 * exact ce împiedică propria noastră derulare să stingă inelul.
 *
 * Întoarce dacă a derulat efectiv - de asta depinde CÂND decidem mutarea
 * panoului: fără derulare, geometria e finală acum; cu derulare, ținta mai
 * alunecă câteva sute de ms și orice decizie luată imediat ar muta panoul
 * pentru o suprapunere pe care derularea o rezolvă oricum.
 */
function scrollIntoViewIfNeeded(el: Element): boolean {
  const vh = window.innerHeight || document.documentElement.clientHeight;
  if (vh <= 0) return false; // fără viewport citibil nu tragem nicio concluzie
  const rect = el.getBoundingClientRect();
  if (rect.top >= 0 && rect.bottom <= vh) return false;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  return true;
}

interface SpotlightInstance {
  /** Vizitatorul a redeschis panoul dochat: pentru inelul ăsta nu-l mai dochăm. */
  dockReleased?: boolean;
  host: HTMLElement;
  ring: HTMLDivElement;
  label?: HTMLDivElement;
  target: Element;
  /** Strămoșii cu `overflow` != `visible`, calculați o dată la afișare. */
  clippers: Element[];
  resizeObserver?: ResizeObserver;
  rafId: number | null;
  armTimer: ReturnType<typeof setTimeout> | null;
  dismissTimer: ReturnType<typeof setTimeout> | null;
  armed: boolean;
  shownAt: number;
  onScroll: () => void;
  onClick: (event: Event) => void;
  onResize: () => void;
}

let active: SpotlightInstance | null = null;

/**
 * Anulează fereastra de „liniște" DOM aflată în curs (dacă există), fără să
 * cheme activarea. Ținut la nivel de modul, nu pe instanță: până la activare,
 * spotlight-ul e deja demontat (vezi `onClick` mai jos), deci nu mai există o
 * `SpotlightInstance` de care să agățăm anularea.
 */
let cancelPendingSettle: (() => void) | null = null;

/**
 * Fereastra de „liniște" DOM înainte de a chema activarea: un click pe
 * elementul evidențiat poate declanșa un reveal animat sau încărcat lazy
 * (accordion, tab, dropdown) - o recapturare imediată a paginii ar vedea încă
 * DOM-ul vechi. Mecanismul stă în `settle.ts`, o variantă runtime mică a ideii
 * din `builder/scan.ts` - NU importată de-acolo: acela e un bundle separat,
 * încărcat lazy doar pentru admin, iar codul care rulează la fiecare vizitator
 * nu are voie să-l tragă după el.
 */
function settleThenActivate(callback: () => void): void {
  // Mecanismul comun stă în `settle.ts` (îl folosește și conversația vocală
  // după navigare). Aici rămâne doar ce e specific spotlight-ului: ținem
  // anularea la îndemână, fiindcă un dismiss (vizitatorul dă click în altă
  // parte) trebuie s-o poată opri înainte de activare.
  const cancel = settleDom(() => {
    cancelPendingSettle = null;
    callback();
  });
  cancelPendingSettle = () => {
    cancel();
    cancelPendingSettle = null;
  };
}

/**
 * Cât timp rămâne armată activarea după ce inelul s-a stins.
 *
 * 30s (valoarea inițială) s-a dovedit prea strâns la testele live din 19 iul
 * 2026: de când ghidajul trece și peste pagini, răspunsul care evidențiază un
 * link e adesea citit, comparat cu pagina, rumegat - iar clickul vine după
 * minutul unu. Cu activarea expirată, vizitatorul apasă un link încă evidențiat
 * vizual și lanțul de ghidare moare în tăcere: navigarea se întâmplă, dar
 * continuarea nu mai vine. Exact hop-ul „premium", pierdut pe cei mai atenți
 * vizitatori.
 *
 * 2 minute (19 iul - 22 aug 2026) țineau fereastra deschisă cât o citire
 * lentă. De când inelul rămâne pe ecran până la click (`RING_DISMISS_MS`), cele
 * două trebuie să expire ÎMPREUNĂ: un inel încă vizibil cu activarea moartă ar
 * fi exact bugul de la 19 iul, doar mai târziu. Riscul de „click întâmplător
 * devenit continuare" e ținut de celelalte gărzi: recaptura compară
 * snapshot-ul (un click care nu schimbă nimic nu declanșează nimic), intenția
 * cross-page are propriul TTL de 60s de la click, iar serverul plafonează
 * oricum la 2 continuări.
 */
const ACTIVATION_TTL_MS = RING_DISMISS_MS;

interface ArmedActivation {
  target: Element;
  handler: (event: Event) => void;
  timer: ReturnType<typeof setTimeout>;
}

let armedActivation: ArmedActivation | null = null;

/**
 * Dezarmează activarea în așteptare, dacă există. Idempotentă.
 *
 * `reason` decide DOAR dacă rezultă telemetrie - nu schimbă dezarmarea în
 * sine, care se întâmplă necondiționat:
 *  - un motiv "abandoned_*" emite prin sink (dacă e legat) - regula centrală:
 *    un abandon există DOAR când exista o activare armată de dezarmat.
 *  - "reached" și "superseded" nu emit nimic aici. "reached" se raportează
 *    direct din sdk.ts (acolo unde se știe sigur că pasul a fost atins, nu
 *    doar că activarea s-a dezarmat); "superseded" înseamnă că pasul următor
 *    al ACELEIAȘI ghidări înlocuiește highlight-ul vechi - nu un abandon.
 *  - fără `reason` (apeluri vechi/de compatibilitate) nu emite nimic.
 */
export function disarmSpotlightActivation(reason?: SpotlightDisarmReason): void {
  if (!armedActivation) return;
  const armed = armedActivation;
  armedActivation = null;
  clearTimeout(armed.timer);
  armed.target.removeEventListener("click", armed.handler);
  if (reason && ABANDON_REASONS.has(reason) && guidanceOutcomeSink) {
    guidanceOutcomeSink(
      reason as "abandoned_dismissed" | "abandoned_expired" | "abandoned_new_question",
      armedTargetLabel(armed.target),
    );
  }
}

/**
 * Abonează un callback la activare: vizitatorul dă click PE elementul
 * evidențiat (sau pe un descendent al lui - o iconiță dintr-un buton).
 *
 * Ascultătorul stă pe ELEMENT, nu pe instanța spotlight-ului, și supraviețuiește
 * deliberat stingerii inelului (până pe 22 aug 2026 inelul se demonta la primul
 * scroll; acum nu, dar decuplarea rămâne corectă: intenția e aceeași fie că
 * inelul mai strălucește sau nu, iar un inel stins de altceva nu are voie să
 * rupă firul).
 *
 * Se dezarmează singur: după activare (`once`), după TTL, la un highlight nou
 * (pasul următor îl înlocuiește pe cel vechi) și la începutul fiecărei întrebări
 * noi (sdk.ts) - altfel un răspuns fără highlight ar lăsa armarea precedentă
 * atârnând.
 *
 * Întoarce `false` dacă nu există niciun spotlight activ ACUM: răspunsul curent
 * n-a evidențiat nimic, deci n-are la ce se abona apelantul.
 *
 * `onClickThrough` (opțional) rulează SINCRON în handlerul de click, înainte de
 * fereastra de liniște: dacă acel click descarcă pagina (un `<a>` care
 * navighează), e singura fereastră garantată înainte de unload - acolo apelantul
 * apucă să persiste ce trebuie să supraviețuiască reload-ului. `callback`
 * (settled) rulează abia după ce DOM-ul s-a liniștit, deci DOAR dacă pagina a
 * supraviețuit (SPA sau dezvăluire in-page).
 */
export function onSpotlightActivate(
  callback: () => void,
  onClickThrough?: (el: Element) => void,
): boolean {
  if (!active) return false;
  const target = active.target;
  // Orice activare rămasă armată de mai devreme e înlocuită acum de asta:
  // pasul următor al ACELEIAȘI ghidări, nu un abandon.
  disarmSpotlightActivation("superseded");

  const handler = (event: Event): void => {
    disarmSpotlightActivation("reached");
    // Sincron, înaintea lui settleThenActivate: dacă unload-ul vine, asta e
    // ultima linie care mai rulează. Niciodată preventDefault - vizitatorul
    // navighează el, noi doar notăm intenția. Elementul pe care s-a dat efectiv
    // click (poate un descendent al țintei - o iconiță dintr-un link), ca
    // apelantul să poată urca la `a[href]` cu `closest`.
    if (onClickThrough) onClickThrough((event.target as Element | null) ?? target);
    settleThenActivate(callback);
  };
  armedActivation = {
    target,
    handler,
    timer: setTimeout(() => disarmSpotlightActivation("abandoned_expired"), ACTIVATION_TTL_MS),
  };
  // Faza de bubbling, ca un click pe un descendent să urce până aici; `once`
  // face activarea structural unică.
  target.addEventListener("click", handler, { once: true });
  return true;
}

/** Coalesează recalculările de geometrie pe un singur rAF per „val" de evenimente. */
function scheduleGeometryUpdate(instance: SpotlightInstance): void {
  if (instance.rafId != null) return;
  instance.rafId = requestAnimationFrame(() => {
    instance.rafId = null;
    updateGeometry(instance);
  });
}

/** Poziționează bula lângă inel: preferă dedesubt, trece deasupra dacă nu încape, clampată orizontal. */
function positionLabel(
  instance: SpotlightInstance,
  ringLeft: number,
  ringTop: number,
  ringWidth: number,
  ringHeight: number,
): void {
  const label = instance.label;
  if (!label) return;
  const rect = label.getBoundingClientRect();
  const labelWidth = rect.width || label.offsetWidth;
  const labelHeight = rect.height || label.offsetHeight;

  let top = ringTop + ringHeight + LABEL_GAP_PX;
  if (top + labelHeight > window.innerHeight) {
    top = ringTop - labelHeight - LABEL_GAP_PX;
  }
  top = Math.max(VIEWPORT_MARGIN_PX, top);

  let left = ringLeft + ringWidth / 2 - labelWidth / 2;
  const maxLeft = Math.max(VIEWPORT_MARGIN_PX, window.innerWidth - labelWidth - VIEWPORT_MARGIN_PX);
  left = Math.min(Math.max(left, VIEWPORT_MARGIN_PX), maxLeft);

  label.style.top = `${top}px`;
  label.style.left = `${left}px`;
}

/**
 * Geometria vine mereu din elementul viu - nicio coordonată nu vine de la LLM.
 *
 * Trei situații în care nu desenăm nimic, în loc să desenăm greșit: ținta a
 * dispărut din DOM (îndrumarea e învechită - demontăm de tot), ținta e
 * `display: none`, sau un container o taie complet. Vizibilitatea se
 * recalculează la fiecare actualizare, deci inelul reapare singur dacă
 * vizitatorul derulează containerul înapoi la ea.
 */
function updateGeometry(instance: SpotlightInstance): void {
  if (!instance.target.isConnected) return dismissSpotlight();

  const visible = visibleExtent(instance);
  instance.host.style.setProperty("display", visible ? "block" : "none", "important");
  if (!visible) return;

  const rect = instance.target.getBoundingClientRect();
  const left = rect.left - RING_OFFSET_PX;
  const top = rect.top - RING_OFFSET_PX;
  const width = Math.max(0, rect.width + RING_OFFSET_PX * 2);
  const height = Math.max(0, rect.height + RING_OFFSET_PX * 2);

  instance.ring.style.left = `${left}px`;
  instance.ring.style.top = `${top}px`;
  instance.ring.style.width = `${width}px`;
  instance.ring.style.height = `${height}px`;

  positionLabel(instance, left, top, width, height);
}

/**
 * Reprogramează armarea dismiss-ului pe scroll la `max(shownAt + GRACE, acum + STABLE)`.
 * Apelată o dată la afișare (armează la +GRACE dacă nu mai vine niciun scroll)
 * și la fiecare eveniment de scroll cât timp nu e încă armat (împinge +STABLE
 * față de ultimul scroll, fără să coboare sub podeaua de grație).
 */
function scheduleArm(instance: SpotlightInstance): void {
  if (instance.armTimer != null) clearTimeout(instance.armTimer);
  const now = performance.now();
  const target = Math.max(instance.shownAt + SCROLL_ARM_GRACE_MS, now + SCROLL_STABLE_MS);
  instance.armTimer = setTimeout(() => {
    instance.armTimer = null;
    instance.armed = true;
    // Momentul în care știm că derularea s-a liniștit - deci singurul în care
    // geometria țintei e finală. Idempotentă față de evaluarea de la afișare.
    if (active === instance) avoidChatObstacle(instance);
  }, target - now);
}

/**
 * E nodul ăsta elementul evidențiat acum (sau un descendent al lui)? Folosit de
 * orb-chat: un pointerdown în afara cardului îl strânge, dar dacă apăsarea e
 * chiar pe pasul arătat, strângerea nu e o închidere a ghidării (22 aug 2026).
 */
export function isSpotlightTarget(node: Node | null): boolean {
  return active !== null && node !== null && active.target.contains(node);
}

/** Demontează complet spotlight-ul activ: nod DOM, listeners, observer, rAF, timer. Idempotentă. */
export function dismissSpotlight(): void {
  // O fereastră de „liniște" în curs (după un click de activare) nu mai are
  // sens fără spotlight-ul care a pornit-o - de-aia asta rulează necondiționat,
  // înaintea gărzii `if (!active)` de mai jos.
  cancelPendingSettle?.();
  if (!active) return;
  const instance = active;
  active = null;
  // Panoul se întoarce la locul lui: mutarea a fost împrumutată pentru ACEST
  // inel, iar fără inel n-are de ce să mai stea deoparte.
  chatObstacle?.shift(0, 0);
  chatObstacle?.setDocked?.(false);

  if (instance.rafId != null) cancelAnimationFrame(instance.rafId);
  if (instance.armTimer != null) clearTimeout(instance.armTimer);
  if (instance.dismissTimer != null) clearTimeout(instance.dismissTimer);
  instance.resizeObserver?.disconnect();
  window.removeEventListener("scroll", instance.onScroll, true);
  instance.target.removeEventListener("click", instance.onClick);
  window.removeEventListener("resize", instance.onResize);
  instance.host.remove();
}

function buildLabel(title?: string, description?: string): HTMLDivElement | undefined {
  if (!title && !description) return undefined;
  const label = document.createElement("div");
  label.className = "aig-spotlight-label";
  // Text randat prin textContent - niciodată innerHTML - deci un titlu care
  // arată ca `<script>` rămâne text inert, nu produce un nod nou.
  if (title) {
    const t = document.createElement("div");
    t.className = "aig-spotlight-label-title";
    t.textContent = title;
    label.appendChild(t);
  }
  if (description) {
    const d = document.createElement("div");
    d.textContent = description;
    label.appendChild(d);
  }
  return label;
}

export function highlightElement(element: Element, title?: string, description?: string): void {
  // Pasul nou îl înlocuiește pe cel vechi: o activare rămasă armată pe ținta
  // precedentă e înlocuită, nu abandonată - pasul următor al ACELEIAȘI
  // ghidări preia firul.
  disarmSpotlightActivation("superseded");
  dismissSpotlight();

  const host = document.createElement("div");
  host.setAttribute("data-ai-guidance", "spotlight");
  // Inline + !important, ca în widget.ts: bate orice regulă globală a
  // paginii gazdă, indiferent de specificitate sau !important propriu.
  host.style.setProperty("position", "fixed", "important");
  host.style.setProperty("inset", "0", "important");
  host.style.setProperty("pointer-events", "none", "important");
  host.style.setProperty("z-index", String(SPOTLIGHT_Z_INDEX), "important");

  const primary = currentPrimaryColor();
  host.style.setProperty("--aig-primary", primary);
  host.style.setProperty("--aig-primary-rgb", hexToRgbChannels(primary));

  const shadow = host.attachShadow({ mode: "open" });
  const style = document.createElement("style");
  style.textContent = SPOTLIGHT_CSS;
  shadow.appendChild(style);

  const ring = document.createElement("div");
  ring.className = "aig-spotlight-ring";
  shadow.appendChild(ring);

  const label = buildLabel(title, description);
  if (label) shadow.appendChild(label);

  document.body.appendChild(host);

  const instance: SpotlightInstance = {
    host,
    ring,
    label,
    target: element,
    clippers: clippingAncestors(element),
    rafId: null,
    armTimer: null,
    dismissTimer: null,
    armed: false,
    shownAt: performance.now(),
    onScroll: () => undefined,
    onClick: () => undefined,
    onResize: () => undefined,
  };

  // Scroll-ul NU mai stinge inelul (21 aug 2026, feedback Andrei): a derula
  // ca să citești în jurul elementului e gestul firesc, iar inelul se mută
  // odată cu ținta. `scheduleArm` rămâne pentru momentul „derularea s-a
  // liniștit", în care panoul se ferește de inel (`avoidChatObstacle`).
  instance.onScroll = () => {
    scheduleGeometryUpdate(instance);
    if (!instance.armed) scheduleArm(instance);
  };
  // Doar un click PE elementul evidențiat stinge inelul - nu unul oriunde pe
  // pagină (vezi RING_DISMISS_MS). Activarea NU se decide aici: ea stă pe un
  // ascultător propriu, pe elementul-țintă (vezi `onSpotlightActivate`), tocmai
  // ca să supraviețuiască stingerii - inclusiv celei provocate de un scroll.
  instance.onClick = () => dismissSpotlight();
  // La resize se schimbă și viewportul, deci un panou mutat mai devreme poate
  // ajunge peste marginea ecranului sau, dimpotrivă, să nu mai fie în cale.
  instance.onResize = () => {
    scheduleGeometryUpdate(instance);
    avoidChatObstacle(instance);
  };

  if (typeof ResizeObserver !== "undefined") {
    instance.resizeObserver = new ResizeObserver(() => scheduleGeometryUpdate(instance));
    instance.resizeObserver.observe(element);
  }

  // capture: true - prinde și scroll-ul unui container ascuns pe drum, nu
  // doar cel de pe window (scroll-ul nu urcă prin bubbling, dar capturarea
  // pornește de la window în jos, deci trece prin el oricum).
  window.addEventListener("scroll", instance.onScroll, { passive: true, capture: true });
  // Pe ELEMENT, nu pe window - un click pe un descendent (icon dintr-un buton)
  // tot urcă până aici prin bubbling.
  element.addEventListener("click", instance.onClick);
  window.addEventListener("resize", instance.onResize);
  instance.dismissTimer = setTimeout(() => dismissSpotlight(), RING_DISMISS_MS);

  active = instance;

  // Raza o dată, aici - nu în bucla de geometrie (vezi ringRadiusPx).
  ring.style.borderRadius = `${ringRadiusPx(element)}px`;
  const scrolled = scrollIntoViewIfNeeded(element);
  updateGeometry(instance);
  // Fără derulare, geometria de acum e cea finală: decidem pe loc, ca vizitatorul
  // să nu apuce să se uite la un inel ascuns sub chat. Cu derulare, decizia
  // așteaptă liniștea (vezi scheduleArm) - altfel am muta pentru o suprapunere
  // pe care derularea o desface singură.
  if (!scrolled) avoidChatObstacle(instance);
  scheduleArm(instance);
}
